import { defineStore } from 'pinia'
import { ref } from 'vue'
import { requestGetRolesPermissions } from '@/api/login/permission'
import {
  EnumWxMpPermissionMenuType,
  MenuOptionItemObj,
  MenuOptionList,
  WxMpPermissionMenuGroupInfo,
  WxMpPermissionMenuInfo,
  WxMpPermissionMenuItemInfo,
  WxMpPermissionMenuRoleInfo,
} from '@/api/login/permissionType'
import { EnumIdType } from '@/typings'

// 权限菜单 Store
export const usePermissionStore = defineStore(
  'permissionStore',
  () => {
    // 原始权限菜单数据（接口返回）
    const rawMenuData = ref<WxMpPermissionMenuInfo[]>([])

    // 组装后的首页菜单列表
    const homeMenuList = ref<MenuOptionList>([])

    // 菜单是否已加载
    const isMenuLoaded = ref(false)

    // 菜单加载中状态
    const isMenuLoading = ref(false)

    /**
     * 获取权限菜单数据
     */
    async function fetchPermissionMenu() {
      if (isMenuLoading.value) {
        return
      }

      try {
        isMenuLoading.value = true
        const { data } = await requestGetRolesPermissions()
        rawMenuData.value = data
        // 组装菜单数据
        assembleMenuList(data)
        isMenuLoaded.value = true
      }
      catch (error) {
        console.error('获取权限菜单失败:', error)
        throw error
      }
      finally {
        isMenuLoading.value = false
      }
    }

    /**
     * 组装菜单列表
     * 将扁平的接口数据转换为树形结构
     */
    function assembleMenuList(menuData: WxMpPermissionMenuInfo[]) {
      // 分离不同类型的菜单数据
      const roleItems: WxMpPermissionMenuRoleInfo[] = []
      const groupItems: WxMpPermissionMenuGroupInfo[] = []
      const menuItems: WxMpPermissionMenuItemInfo[] = []

      menuData.forEach((item) => {
        // 过滤禁用的菜单
        if (!item.enabled) {
          return
        }

        switch (item.type) {
          case EnumWxMpPermissionMenuType.ROLE_LEVEL:
            roleItems.push(item as WxMpPermissionMenuRoleInfo)
            break
          case EnumWxMpPermissionMenuType.GROUP_LEVEL:
            groupItems.push(item as WxMpPermissionMenuGroupInfo)
            break
          case EnumWxMpPermissionMenuType.MENU_ITEM:
            menuItems.push(item as WxMpPermissionMenuItemInfo)
            break
        }
      })

      // 按 sortOrder 排序
      roleItems.sort((a, b) => a.sortOrder - b.sortOrder)
      groupItems.sort((a, b) => a.sortOrder - b.sortOrder)
      menuItems.sort((a, b) => a.sortOrder - b.sortOrder)

      // 构建菜单树
      const result: MenuOptionList = []

      roleItems.forEach((role) => {
        // 获取该角色下的分组
        const roleGroups = groupItems.filter(group => group.parentId === role.id)

        const roleOption = {
          label: role.name,
          value: role.roleCode,
          options: roleGroups.map((group) => {
            // 获取该分组下的菜单项
            const groupMenus = menuItems.filter(menu => menu.parentId === group.id)

            return {
              label: group.name,
              options: groupMenus.map((menu): MenuOptionItemObj => ({
                title: menu.name,
                iconUrl: menu.iconUrl,
                path: menu.path,
                badgeNum: 0,
              })),
            }
          }),
        }

        result.push(roleOption)
      })

      homeMenuList.value = result
    }

    /**
     * 重置菜单数据
     */
    function resetMenu() {
      rawMenuData.value = []
      homeMenuList.value = []
      isMenuLoaded.value = false
    }

    /**
     * 更新菜单项的角标数量
     */
    function updateMenuBadge(path: string, badgeNum: number) {
      homeMenuList.value.forEach((roleMenu) => {
        roleMenu.options.forEach((group) => {
          const menuItem = group.options.find(item => item.path.includes(path))
          if (menuItem) {
            menuItem.badgeNum = badgeNum
          }
        })
      })
    }

    /**
     * 根据角色获取菜单
     */
    function getMenuByRole(roleCode: EnumIdType): MenuOptionList[number] | undefined {
      return homeMenuList.value.find(item => item.value === roleCode)
    }

    return {
      rawMenuData,
      homeMenuList,
      isMenuLoaded,
      isMenuLoading,
      fetchPermissionMenu,
      resetMenu,
      updateMenuBadge,
      getMenuByRole,
    }
  },
)
